import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import Animation from "@/Shared/Animations/Animation";
import type { UUID } from "@/Shared/types";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { useOnce } from "@/Hooks/useOnce";
import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import moment from "moment";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BookShipmentBranchesPanel from "./Panels/BookShipmentBranchesPanel";
import { BookShipmentInsurancePanel } from "./Panels/BookShipmentInsurancePanel";
import BookShipmentPickupDatePanel from "./Panels/BookShipmentPickupDatePanel";
import BookShipmentReferenceNumbersPanel from "./Panels/BookShipmentReferenceNumbersPanel";
import BookShipmentContentsSection2 from "./CustomComponents/BookShipmentContentsSection2";
import { BookShipmentDetailsDateAnchor } from "./CustomComponents/Internal/bookShipmentDetailsDateAnchor";
import { useBookShipmentDetailsValidation } from "./Hooks/useBookShipmentDetailsValidation";
import { useBookShipmentGetQuotes } from "./Hooks/useBookShipmentGetQuotes";
import { useBookShipmentPickupDateStatus } from "./Hooks/useBookShipmentPickupDateStatus";
import { BookShipmentDetailsLocationSection } from "./Internal/bookShipmentDetailsLocationSection";
import { BookShipmentDetailsSubmitRow } from "./Internal/bookShipmentDetailsSubmitRow";
import {
  addLineItem,
  duplicateLineItem,
  addLineItemFromSavedCommodity,
  onAddInsuranceToShipmentChanged,
  onSetInsuranceAmount,
  onSetInsuranceCurrency,
  rearrangeLengthWidth,
  removeLineItem,
  resetShipment,
  setBranchId,
  setDeliveryReferenceNumber,
  setDeliveryAmazonReferenceId,
  setDeliveryAmazonShipmentId,
  setPickupDate,
  setPickupReferenceNumber,
  setStateOfGoods,
  updateLineItem,
} from "@/Slices/bookShipmentSlice";
import { useBookShipment2Slice } from "@/Hooks/useBookShipment2Slice";
import type { RootState } from "@/store";

interface BookShipmentDetailsScreenProps {
  title?: ReactNode | undefined;
  description?: ReactNode | undefined;
  defaultForceValidation?: boolean | undefined;
  savedLocations: Array<SavedLocation>;
  savedCommodities: Array<SavedCommodity>;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
}

function BookShipmentDetailsScreen(props: BookShipmentDetailsScreenProps) {
  const dispatch = useDispatch();
  const [forceValidation, setForceValidation] = useState(
    props.defaultForceValidation ?? false,
  );
  const [nmfcErrors, setNmfcErrors] = useState(new Set<UUID>());

  const { isAuthenticated } = useAuthentication();
  const {
    savedLocations,
    savedCommodities,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
    title = "Get Instant Quotes",
    description = "Enter your shipment details to get accurate quotes from dozens of the best carriers in North America.",
  } = props;

  const [branches, setBranches] = useState<SavedLocation[]>([]);
  const [currentUserBranchId, setCurrentUserBranchId] = useState<
    UUID | undefined
  >();
  const savedLocationApi = useSavedLocationsApi();

  async function loadBranches() {
    if (isAuthenticated) {
      const response = await savedLocationApi.getBranches();
      setBranches(response.items);
      setCurrentUserBranchId(response.currentUserBranchId);
    }
  }

  useOnce(loadBranches);

  const shipmentDraftId = useSelector(
    (state: RootState) => state.bookShipment.draft?.shipmentDraftId,
  );

  const replacesId = useSelector(
    (state: RootState) => state.bookShipment.replacesId,
  );

  const {
    shipment,
    selectedPickupSavedLocationId,
    selectedDeliverySavedLocationId,
    enteringNewPickup,
    enteringNewDelivery,
  } = useBookShipment2Slice();
  const {
    pickupLocation,
    deliveryLocation,
    pickupDate,
    lineItems,
    pickupReferenceNumber,
    deliveryReferenceNumber,
    deliveryAmazonReferenceId,
    deliveryAmazonShipmentId,
    addInsuranceToShipment,
    insuranceAmount,
    insuranceCurrency,
    stateOfGoods,
    branchId,
  } = shipment;
  ////////////////////////////////////////////////////////////

  let i = 1;

  const memoSetPickupDate = useCallback(
    (newPickupDate: moment.Moment) => {
      dispatch(setPickupDate(newPickupDate.format("YYYY-MM-DD")));
    },
    [dispatch],
  );

  const memoPickupDate = useMemo(() => moment(pickupDate), [pickupDate]);

  const validation = useBookShipmentDetailsValidation({
    shipment,
    branches,
    nmfcErrors,
  });

  const pickupDateStatus = useBookShipmentPickupDateStatus(shipment);

  const getQuotes = useBookShipmentGetQuotes({
    shipment,
    shipmentDraftId,
    replacesId,
    isValid: validation.isValid,
    onValidationFailed: () => setForceValidation(true),
  });

  useEffect(
    function () {
      if (isAuthenticated) {
        dispatch(setBranchId(currentUserBranchId));
      }
    },

    [shipment.shipmentId, currentUserBranchId, isAuthenticated],
  );

  useEffect(
    function () {
      // If there are no saved commodities, and there are no line items,
      // then make a line item
      if (lineItems.length === 0 && savedCommodities.length === 0) {
        dispatch(addLineItem());
      }
    },

    [shipment.shipmentId],
  );

  // The confirmation lives on the Button's `confirm` prop, so this only runs
  // once the user has said yes.
  function onReset() {
    dispatch(
      resetShipment({
        savedLocations,
        defaultPickupLocationId,
        defaultDeliveryLocationId,
      }),
    );
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.location.reload();
    }, 700);
  }

  function onAddLineItemFromSavedCommodity(sc: SavedCommodity) {
    dispatch(addLineItemFromSavedCommodity(sc));
  }

  function onDuplicateLineItem(lineItemId: UUID) {
    dispatch(duplicateLineItem(lineItemId));
  }

  const showErrorsAtBottom = forceValidation && !validation.isValid;

  return (
    // The whole quoting form uses the spacious underline look. ui-core controls
    // default to boxed, so set the presentation once for the screen — every
    // control in every section inherits it.
    <ControlPresentationProvider presentation="underlined">
      {getQuotes.dialogs}
      <Stack align="center">
        <Animation
          width={150}
          height={150}
          lottieFilesUrl="https://lottiefiles.com/28010-length-measure-tape"
          pathToAnimation="/animations/measure.json"
          loop
        />
        <T size="3xl" weight="bold">
          {title}
        </T>
        <T size="base" color="secondary" align="center">
          {description}
        </T>
        {!isAuthenticated && (
          <T size="sm" color="secondary" align="center">
            ⚠️ FreightSimple does not handle small packages, parcels, vehicles,
            or home moves.
          </T>
        )}
        <BookShipmentDetailsDateAnchor
          anchorRef={pickupDateStatus.datePickerRef}
        >
          <BookShipmentPickupDatePanel
            pickupCountry={shipment.pickupLocation.address.countryCode}
            pickupDate={memoPickupDate}
            setPickupDate={memoSetPickupDate}
            index={i++}
          />
        </BookShipmentDetailsDateAnchor>
        <BookShipmentDetailsLocationSection
          context={LocationContext.Pickup}
          location={pickupLocation}
          selectedSavedLocationId={selectedPickupSavedLocationId}
          enteringNew={enteringNewPickup}
          savedLocations={savedLocations}
          defaultLocationId={defaultPickupLocationId}
          errorMessages={validation.errorMessagesForPickup}
          warningMessages={validation.warningMessagesForPickup}
          forceValidation={forceValidation}
          index={i++}
          disablePostalCodeEntry={getQuotes.showingCreateAccountPopup}
          pickupDate={pickupDate}
        />
        <BookShipmentDetailsLocationSection
          context={LocationContext.Delivery}
          location={deliveryLocation}
          selectedSavedLocationId={selectedDeliverySavedLocationId}
          enteringNew={enteringNewDelivery}
          savedLocations={savedLocations}
          defaultLocationId={defaultDeliveryLocationId}
          errorMessages={validation.errorMessagesForDelivery}
          warningMessages={validation.warningMessagesForDelivery}
          forceValidation={forceValidation}
          index={i++}
          disablePostalCodeEntry={getQuotes.showingCreateAccountPopup}
          pickupDate={pickupDate}
        />
        <BookShipmentContentsSection2
          onUpdateLineItem={function (lineItemId, value) {
            dispatch(updateLineItem({ lineItemId, value }));
          }}
          addAnotherLineItem={function () {
            dispatch(addLineItem());
          }}
          duplicateLineItem={onDuplicateLineItem}
          removeLineItemById={function (lineItemId) {
            setNmfcErrors((s) => {
              s.delete(lineItemId);
              return s;
            });
            dispatch(removeLineItem(lineItemId));
          }}
          savedCommodities={savedCommodities}
          onRearrangeLengthWidth={function (lineItemId) {
            dispatch(rearrangeLengthWidth(lineItemId));
          }}
          lineItems={lineItems}
          forceValidation={forceValidation}
          errorMessageForLineItems={validation.errorMessagesForLineItems}
          overallErrorMessage={validation.overallLineItemsErrorMessage}
          maxLineItems={validation.maxLineItems}
          shouldRenderFreightClass={validation.freightClassRequired}
          index={i++}
          setHasNmfcError={function (lineItemId: UUID, hasNmfcError: boolean) {
            if (hasNmfcError) {
              setNmfcErrors((s) => {
                s.add(lineItemId);
                return s;
              });
            } else {
              setNmfcErrors((s) => {
                s.delete(lineItemId);
                return s;
              });
            }
          }}
          addLineItemFromSavedCommodity={onAddLineItemFromSavedCommodity}
          pickupLocation={pickupLocation}
          deliveryLocation={deliveryLocation}
          protectFromFreezingOutOfSeason={
            validation.protectFromFreezingOutOfSeason
          }
        />
        <BookShipmentInsurancePanel
          index={i++}
          addInsuranceToShipment={addInsuranceToShipment}
          onAddInsuranceToShipmentChanged={function (newValue) {
            dispatch(onAddInsuranceToShipmentChanged(newValue));
          }}
          insuranceAmount={insuranceAmount}
          onSetInsuranceAmount={function (newValue) {
            dispatch(onSetInsuranceAmount(newValue));
          }}
          insuranceCurrency={insuranceCurrency}
          onSetInsuranceCurrency={function (newValue) {
            dispatch(onSetInsuranceCurrency(newValue));
          }}
          forceValidation={forceValidation}
          locationsErrorMessageForInsurance={validation.insuranceErrorMessage}
          stateOfGoods={stateOfGoods}
          setStateOfGoods={(newValue) => dispatch(setStateOfGoods(newValue))}
          stateOfGoodsErrorMessages={validation.stateOfGoodsErrorMessages}
        />
        {isAuthenticated && (
          <BookShipmentReferenceNumbersPanel
            index={i++}
            pickupReferenceNumber={pickupReferenceNumber}
            deliveryReferenceNumber={deliveryReferenceNumber}
            deliveryAmazonReferenceId={deliveryAmazonReferenceId}
            deliveryAmazonShipmentId={deliveryAmazonShipmentId}
            deliveryLocation={deliveryLocation}
            onPickupReferenceNumberChanged={function (newValue) {
              dispatch(setPickupReferenceNumber(newValue));
            }}
            onDeliveryReferenceNumberChanged={function (newValue) {
              dispatch(setDeliveryReferenceNumber(newValue));
            }}
            onDeliveryAmazonReferenceIdChanged={function (newValue) {
              dispatch(setDeliveryAmazonReferenceId(newValue));
            }}
            onDeliveryAmazonShipmentIdChanged={function (newValue) {
              dispatch(setDeliveryAmazonShipmentId(newValue));
            }}
          />
        )}
        {
          // Don't show the branches view unless we are auth'd
          isAuthenticated &&
            // Only show if the current user is not coded to a branch
            currentUserBranchId === undefined &&
            // Only show if we have branches
            branches.length > 0 && (
              <BookShipmentBranchesPanel
                index={i++}
                branchId={branchId}
                onBranchIdChanged={function (newValue) {
                  dispatch(setBranchId(newValue));
                }}
                branches={branches}
                forceValidation={forceValidation}
                errorMessage={validation.branchesErrorMessage}
              />
            )
        }
        <BookShipmentDetailsSubmitRow
          isPickupDateHoliday={pickupDateStatus.isPickupDateHoliday}
          isPickupDateWeekend={pickupDateStatus.isPickupDateWeekend}
          pickupDateIsInThePast={validation.pickupDateIsInThePast}
          forceValidation={forceValidation}
          showErrorsAtBottom={showErrorsAtBottom}
          quoteRequestError={getQuotes.quoteRequestError}
          submittingQuotes={getQuotes.submittingQuotes}
          quotePreparationMessage={getQuotes.quotePreparationMessage}
          warnings={validation.warnings}
          onScrollToPickupDate={pickupDateStatus.scrollToPickupDate}
          onReset={onReset}
          onGetQuotesOrCreateAccount={getQuotes.onGetQuotesOrCreateAccount}
        />
      </Stack>
    </ControlPresentationProvider>
  );
}

export default BookShipmentDetailsScreen;
